import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@volca/ui/components/ui/dialog";
import { Text } from "@volca/ui/components/ui/text";

const WARNING =
  "Each one is applied to ServiceTitan or QuickBooks right away. Actions below 90% confidence, and invoices that still need a job, stay for you to review.";

const PARTIAL_NOTE = "Only the invoices loaded so far are included.";

export const BulkApproveButton = ({
  actionIds,
  partial,
  scope = "",
  disabled = false,
  pending,
  onApprove,
}: {
  actionIds: string[];
  partial: boolean;
  /** What the IDs were picked from (business and filters); a change makes an
   * open dialog's IDs out of date, even when the new result is already in. */
  scope?: string;
  /** The IDs may be stale, e.g. while the list catches up with a new search. */
  disabled?: boolean;
  pending: boolean;
  onApprove: (ids: string[]) => Promise<unknown>;
}) => {
  const [confirmingIds, setConfirmingIds] = useState<string[] | null>(null);
  const [openedFor, setOpenedFor] = useState(scope);
  // `pending` is read off the current queue, which can drop the IDs being
  // approved (filters changed, or approved actions left it) while the request
  // is still out; the dialog tracks its own submission so Approve can't fire
  // twice.
  const [submitting, setSubmitting] = useState(false);
  const busy = pending || submitting;
  // The dialog confirms the IDs it opened with. Once the filters change under
  // it (even back to a result already loaded, so never `disabled`), those are
  // no longer current: it can't confirm them, and it closes as soon as nothing
  // is in flight.
  const [outdated, setOutdated] = useState(false);
  if (confirmingIds !== null && (disabled || scope !== openedFor) && !outdated)
    setOutdated(true);
  if (confirmingIds !== null && outdated && !busy) {
    setConfirmingIds(null);
    setOutdated(false);
  }
  const cancelRef = useRef<HTMLButtonElement>(null);
  const count = (confirmingIds ?? actionIds).length;
  const message = partial ? `${WARNING} ${PARTIAL_NOTE}` : WARNING;

  const confirm = () => {
    if (!confirmingIds || busy || outdated) return;
    setSubmitting(true);
    // On failure the mutation says what went through, and the dialog stays
    // open to try again for the rest.
    onApprove(confirmingIds)
      .then(() => setConfirmingIds(null))
      .catch(() => undefined)
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog
      open={confirmingIds !== null}
      onOpenChange={(next) => {
        if (busy || (next && disabled)) return;
        setOutdated(false);
        setOpenedFor(scope);
        setConfirmingIds(next ? actionIds : null);
      }}
    >
      {/* A DialogTrigger, so closing the dialog hands focus back here. */}
      <DialogTrigger
        render={
          <Button
            variant="secondary"
            disabled={pending || disabled}
            loading={pending}
          />
        }
      >
        {!pending && <CheckIcon data-icon="inline-start" />}
        Approve {actionIds.length} high-confidence
      </DialogTrigger>
      <DialogContent initialFocus={cancelRef}>
        <DialogHeader className="px-12">
          <DialogTitle>
            {`Approve ${count} high-confidence ${count === 1 ? "action" : "actions"}?`}
          </DialogTitle>
          <DialogDescription className="sr-only">{message}</DialogDescription>
        </DialogHeader>
        <Text tone="muted">{message}</Text>
        <DialogFooter>
          <Button
            ref={cancelRef}
            variant="secondary"
            disabled={busy}
            onClick={() => setConfirmingIds(null)}
          >
            Cancel
          </Button>
          <Button disabled={busy || outdated} loading={busy} onClick={confirm}>
            Approve {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
